import { useEffect, Dispatch } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { browserLocalPersistence, indexedDBLocalPersistence, initializeAuth, getAuth } from 'firebase/auth';
import { AppState, MainContextActionTypes } from '@constants';
import { getEcommerceData } from '@utils/ecommerce';
import { logger } from '@utils/logger';
import { getProductPrices } from '@utils/prices';
import { hydrateInitialState } from '@utils/sessionStorage';
import useLocaleNavigation from '@utils/useLocaleNavigation';

function useLoadEcommerceData(state: AppState, initialStateWithCountry: AppState, dispatch: Dispatch<MainContextActionTypes>) {
  const { navigateToLocaleByCountryCode } = useLocaleNavigation();

  useEffect(() => {
    const initialStateWithSessionStorage: AppState = hydrateInitialState(initialStateWithCountry, ['country']);

    dispatch({
      type: 'REHYDRATE-FROM-SESSION-STORAGE',
      data: initialStateWithSessionStorage
    });

    // Only initialize Firebase if not already initialized in state
    if (!state.firebaseAuth) {
      try {
        // Check if Firebase app already exists to prevent duplicate app error
        let firebaseApp;
        const existingApps = getApps();

        if (existingApps.length > 0) {
          // Use existing app if available
          firebaseApp = getApp();
        } else {
          // Initialize new app
          firebaseApp = initializeApp({
            apiKey: process.env.GOOGLE_API_KEY,
            authDomain: process.env.GOOGLE_AUTH_DOMAIN,
            projectId: process.env.GOOGLE_PROJECT_ID,
            appId: process.env.GOOGLE_APP_ID
          });
        }

        // Initialize auth with persistence settings
        let firebaseAuth;
        if (existingApps.length === 0) {
          // New app - initialize auth with persistence settings
          firebaseAuth = initializeAuth(firebaseApp, {
            persistence: [indexedDBLocalPersistence, browserLocalPersistence]
          });
        } else {
          // Existing app - get the existing auth instance
          firebaseAuth = getAuth(firebaseApp);
        }

        logger.info('✅ Firebase Auth initialized successfully');

        dispatch({ type: 'setFirebaseAuth', data: firebaseAuth });
      } catch (error) {
        logger.error('❌ Firebase initialization failed', error);
      }
    }

    getEcommerceData(initialStateWithCountry.chosenCountry || state.country, dispatch);

    (async () => {
      await getProductPrices();
    })();
  }, []); // Remove dependency to prevent re-runs during re-renders

  useEffect(() => {
    if (state.chosenCountry) {
      if (state.chosenCountry !== state.country) {
        dispatch({ type: 'setCountry', data: state.chosenCountry });

        // Only clear cart if not in email verification flow
        const isEmailVerificationInProgress = sessionStorage.getItem('emailVerificationInProgress') === 'true';
        if (!isEmailVerificationInProgress) {
          dispatch({ type: 'cartClear' });
        }

        getEcommerceData(state.chosenCountry, dispatch);
        navigateToLocaleByCountryCode(state.chosenCountry);
      }
    }
  }, [state.chosenCountry, state.country, dispatch, navigateToLocaleByCountryCode]);
}

export default useLoadEcommerceData;
